import { useTranslation } from "react-i18next";

import { QrCode } from "../../components/game/QrCode.js";

/**
 * How the next person gets in: point a phone at the screen.
 *
 * # The code is on screen too, at readable size
 *
 * Always both. Somebody will be on the wrong network, or holding a phone whose
 * camera app has been replaced by something that does not scan, or standing
 * behind the sofa. Four characters somebody can read out loud is the fallback
 * that never fails, and it is the reason the alphabet has no `I`, `O`, `1` or
 * `0` in it (`src/party/code.rs`).
 *
 * The encoder itself moved to `components/game/QrCode` in phase 8, when the
 * recap needed a second one — two QR encoders configured slightly differently is
 * how one of them ends up scanning on the phone it was tested with and on no
 * others.
 */
export function JoinInvite({ code, url }: { code: string; url: string }) {
  const { t } = useTranslation();

  return (
    <section
      aria-label={t("invite.label")}
      className="flex flex-col items-center gap-6 text-center"
    >
      <p className="text-3xl text-white/60">{t("invite.point")}</p>

      <QrCode
        url={url}
        label={t("invite.qr")}
        className="w-72 rounded-2xl bg-stage-900 p-5 [&>svg]:w-full"
      />

      <div>
        <p className="text-2xl text-white/50">{t("invite.orType")}</p>
        <p className="text-3xl text-white/70">{hostOf(url)}</p>
        <p className="mt-3 text-8xl font-semibold tracking-[0.15em] text-flash-400">
          {code}
        </p>
      </div>
    </section>
  );
}

/** The bit somebody has to type, without the scheme. */
function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
